import { useNavigate } from 'react-router-dom'
import { Swiper, SwiperSlide } from 'swiper/react'
import SwiperCore, {
  Keyboard,
  Mousewheel,
  Navigation,
  Pagination,
  Autoplay,
} from 'swiper'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import Typography from '@mui/material/Typography'
import LockIcon from '@mui/icons-material/Lock'
import { AssignmentIcon, CorrectIcon } from 'src/v3/core/icons'
import { Box, Button, Tooltip } from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery'

import { always, compose, concat, mergeDeepWith } from 'ramda'
import { useAppSelector } from 'src/store/hooks'
import {
  selectCoursesGroupedBySection,
  selectSectionsSorted,
  selectCourses,
  selectSeries,
  selectClassesStatus,
  selectCompletedClassesStatus,
  selectCompletedCoursesStatus,
  selectCoursesStatus,
  selectInProgressCoursesStatus,
  selectSeriesStatus,
  selectCompletedCourses,
  selectIncompleteAssignmentCourses,
  selectIncompleteAssignmentCoursesStatus,
} from 'src/v3/self-paced/self-paced.slice'
import { ReactElement, useState } from 'react'
import useSession from 'src/authentication/hooks/use-session'
import noop from 'src/utilities/noop'
import withConfirm from 'src/components/common/with-confirm'
import { Course } from 'src/v3/core/types'
import Loading from 'src/v3/components/loading'

SwiperCore.use([Autoplay])

export const completeMarker = (
  <Box
    sx={{
      position: 'absolute',
      zIndex: 1,
      right: 3,
      top: 3,
      bgcolor: 'background.default',
      borderRadius: '50%',
    }}
  >
    <Tooltip title="You have completed this course" placement="top">
      <CorrectIcon
        sx={{
          mt: '-3px',
          mx: '1.5px',
          color: 'success.main',
        }}
      />
    </Tooltip>
  </Box>
)

export const assignedMarker = (
  <Box
    sx={{
      position: 'absolute',
      zIndex: 1,
      right: 3,
      top: 3,
      bgcolor: 'background.default',
      borderRadius: '50%',
    }}
  >
    <Tooltip title="You have been assigned this course" placement="top">
      <AssignmentIcon
        sx={{
          mt: '-2px',
          mx: '1.5px',
          color: 'primary.main',
        }}
      />
    </Tooltip>
  </Box>
)

const boundtreeSectionID = '9310c4d1-7447-47d6-a01a-60f1b87d68ac'

let boundTreeFreeCourseIDs = [
  'b4995e14-8480-4b5d-99a7-aa814b0c45d1',
  '039c7217-4172-474f-b4c1-f47bcab67310',
  '0646715f-54ac-45d0-a649-a72b6cacda60',
  'b85cd107-e5f1-4ecb-a0dd-e9e3fda4ad0d',
  '3bd2fcc3-80f7-4486-9b72-8b2c7f652148',

  'ca1ddd61-6a0c-43ff-902d-21ab2a791f26',
  '348d5270-dfdb-408d-a255-4e91ba79d99d',
  '0c458a7b-7def-47f6-998d-7179a350fdc9',
  'dce6541e-2d57-45a8-a888-6cc41fb07ea4',
  '49391a29-840b-4c31-b51e-965dd27279bd',
]

export default (): ReactElement => {
  let navigate = useNavigate()
  let session = useSession()

  let sections = useAppSelector(selectSectionsSorted)
  let courses = useAppSelector(selectCoursesGroupedBySection)
  let allCourses = useAppSelector(selectCourses)
  let series = useAppSelector(selectSeries)
  let completedCourses = useAppSelector(selectCompletedCourses)
  let incompleteAssignmentCourses = useAppSelector(
    selectIncompleteAssignmentCourses,
  )

  let classesStatus = useAppSelector(selectClassesStatus)
  let completedClassesStatus = useAppSelector(selectCompletedClassesStatus)
  let completedCoursesStatus = useAppSelector(selectCompletedCoursesStatus)
  let coursesStatus = useAppSelector(selectCoursesStatus)
  let inProgressCoursesStatus = useAppSelector(selectInProgressCoursesStatus)
  let seriesStatus = useAppSelector(selectSeriesStatus)
  let incompleteAssignmentCoursesStatus = useAppSelector(
    selectIncompleteAssignmentCoursesStatus,
  )

  let largeScreen = useMediaQuery('(min-width:1000px)')

  let [confirmStatus, setConfirmStatus] = useState<'Closed' | 'Open'>('Closed')

  let ConfirmUpgrade = withConfirm(
    'Upgrade to Full Access',
    'IMPORTANT: You once you pay for full access, you will need to log out of Studio and back in to access all of the content.',
    confirmStatus === 'Open',
    compose(setConfirmStatus, always('Closed') as any),
    () => {
      Promise.resolve().then(() =>
        navigate(
          `/subscribe-boundtree?email=${session?.emailAddress
            .toLowerCase()
            .trim()}`,
        ),
      )
    },
  )

  let boundtreeCourses = allCourses.filter((course) =>
    boundTreeFreeCourseIDs.includes(course.id),
  )

  return (
    <>
      <Swiper
        cssMode={true}
        mousewheel={true}
        className="main-carousel"
        loop
        navigation
        pagination={{
          clickable: true,
        }}
        modules={[Navigation, Pagination]}
        style={{ zIndex: 0 }}
      >
        {session?.boundTreeStatus === 'limited' ? (
          <>
            <SwiperSlide>
              <img
                style={{ cursor: 'pointer' }}
                onClick={compose(setConfirmStatus, always('Open') as any)}
                src={`${import.meta.env.VITE_CLOUDFRONT_URL}/public/BoundTreeFullAccess.png`}
              />
            </SwiperSlide>
            <SwiperSlide>
              <img
                style={{ cursor: 'pointer' }}
                onClick={() =>
                  window.open('https://www.boundtree.com/education', '_blank')
                }
                src={`${import.meta.env.VITE_CLOUDFRONT_URL}/public/BoundTreeService.png`}
              />
            </SwiperSlide>
          </>
        ) : (
          <>
            <SwiperSlide>
              <img
                style={{ cursor: 'pointer' }}
                onClick={() => navigate('/self-paced/continue-watching')}
                src={`${import.meta.env.VITE_CLOUDFRONT_URL}/public/ContinueWatching.jpg`}
              />
            </SwiperSlide>
            <SwiperSlide>
              <img
                style={{ cursor: 'pointer' }}
                onClick={() => navigate('/self-paced/assignments')}
                src={`${import.meta.env.VITE_CLOUDFRONT_URL}/public/Assignments.png`}
              />
            </SwiperSlide>
            <SwiperSlide>
              <img
                style={{ cursor: 'pointer' }}
                onClick={() =>
                  navigate(
                    '/self-paced/section/71c5d72b-3d17-438d-a692-649779e3b020',
                  )
                }
                src={`${import.meta.env.VITE_CLOUDFRONT_URL}/public/ARV.png`}
              />
            </SwiperSlide>
            <SwiperSlide>
              <img
                style={{ cursor: 'pointer' }}
                onClick={() =>
                  navigate(
                    '/self-paced/section/e1a3c7bb-11ff-4af5-821f-8efbaae3b3d4',
                  )
                }
                src={`${import.meta.env.VITE_CLOUDFRONT_URL}/public/Cardiovascular.png`}
              />
            </SwiperSlide>
            <SwiperSlide>
              <img
                style={{ cursor: 'pointer' }}
                onClick={() =>
                  navigate(
                    '/self-paced/section/1acfde29-b316-4ec7-8500-865f6251303c',
                  )
                }
                src={`${import.meta.env.VITE_CLOUDFRONT_URL}/public/Trauma.png`}
              />
            </SwiperSlide>
            <SwiperSlide>
              <img
                style={{ cursor: 'pointer' }}
                onClick={() =>
                  navigate(
                    '/self-paced/section/a7ce1591-f5cc-445d-90a2-2a61de56a467',
                  )
                }
                src={`${import.meta.env.VITE_CLOUDFRONT_URL}/public/Medical.png`}
              />
            </SwiperSlide>
            <SwiperSlide>
              <img
                style={{ cursor: 'pointer' }}
                onClick={() =>
                  navigate(
                    '/self-paced/section/de34454e-471e-4d25-9c1d-1cf0dfb60875',
                  )
                }
                src={`${import.meta.env.VITE_CLOUDFRONT_URL}/public/Pediatric.png`}
              />
            </SwiperSlide>
            <SwiperSlide>
              <img
                style={{ cursor: 'pointer' }}
                onClick={() =>
                  navigate(
                    '/self-paced/section/73e8b4ee-a1d3-4b77-95cc-5e263b2fc717',
                  )
                }
                src={`${import.meta.env.VITE_CLOUDFRONT_URL}/public/Operations.png`}
              />
            </SwiperSlide>
            <SwiperSlide>
              <img
                style={{ cursor: 'pointer' }}
                onClick={() =>
                  navigate(
                    '/self-paced/section/ca3d02f1-ad3f-4aa2-9e6d-6c4dc3d965f7',
                  )
                }
                src={`${import.meta.env.VITE_CLOUDFRONT_URL}/public/CriticalCare.png`}
              />
            </SwiperSlide>
            <SwiperSlide>
              <img
                style={{ cursor: 'pointer' }}
                onClick={() => navigate('/live')}
                src={`${import.meta.env.VITE_CLOUDFRONT_URL}/public/Live.png`}
              />
            </SwiperSlide>
          </>
        )}
      </Swiper>

      {session?.boundTreeStatus === 'limited' && (
        <div className="mt-4">
          <div className="flex">
            <Typography
              data-testid="free-courses-heading"
              variant="h5"
              className="py-2 ml-2"
              style={{ color: 'white', fontFamily: 'smileAndWave' }}
            >
              Free Courses
            </Typography>
          </div>
          <Swiper
            cssMode
            navigation
            mousewheel
            slidesPerView={largeScreen ? 5 : 3}
            spaceBetween={10}
            slidesPerGroup={2}
            modules={[Navigation, Pagination, Mousewheel, Keyboard]}
            className="netflix-slider"
            data-testid="free-courses-slider"
          >
            {boundtreeCourses.map((item: Course) => (
              <SwiperSlide key={item.id}>
                <img
                  onClick={() =>
                    navigate(
                      `/self-paced/course/${
                        allCourses.find((x) => x.id === item.id)?.id
                      }`,
                    )
                  }
                  style={{ cursor: 'pointer' }}
                  src={`${import.meta.env.VITE_CLOUDFRONT_URL}/public/${item.imageName}`}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      )}

      {series.length > 0 && (
        <div className="mt-4">
          <div className="flex">
            <Typography
              variant="h5"
              className="py-2 ml-2"
              style={{ color: 'white', fontFamily: 'smileAndWave' }}
            >
              Series
            </Typography>
          </div>
          <Swiper
            cssMode
            navigation
            mousewheel
            slidesPerView={largeScreen ? 5 : 3}
            spaceBetween={10}
            slidesPerGroup={2}
            modules={[Navigation, Pagination, Mousewheel, Keyboard]}
            className="netflix-slider"
            data-testid="series-slider"
          >
            {series.map((item) => (
              <SwiperSlide key={item.id} style={{ position: 'relative' }}>
                {session?.boundTreeStatus === 'limited' && (
                  <LockIcon
                    sx={{
                      position: 'absolute',
                      zIndex: 1,
                      right: 5,
                      top: 5,
                      cursor: 'not-allowed',
                    }}
                  />
                )}
                <img
                  onClick={
                    session?.boundTreeStatus === 'limited'
                      ? noop
                      : () => navigate(`/self-paced/series/${item.id}`)
                  }
                  style={{
                    cursor:
                      session?.boundTreeStatus === 'limited'
                        ? 'not-allowed'
                        : 'pointer',
                    filter:
                      session?.boundTreeStatus === 'limited'
                        ? 'brightness(30%)'
                        : 'brightness(100%)',
                  }}
                  src={`${import.meta.env.VITE_CLOUDFRONT_URL}/public/${item.thumbnail}`}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      )}

      {sections
        ?.filter((x) => x.id !== boundtreeSectionID) // No Boundtree section
        .map((section) => (
          <div key={section.id} className="mt-4">
            <div className="flex">
              <Typography
                variant="h5"
                className="py-2 ml-2"
                style={{ color: 'white', fontFamily: 'smileAndWave' }}
              >
                {section.name}
              </Typography>
              <Button
                onClick={
                  session?.boundTreeStatus === 'limited'
                    ? noop
                    : () => navigate(`/self-paced/section/${section.id}`)
                }
                variant="text"
                className="ml-4"
                sx={{
                  color:
                    session?.boundTreeStatus === 'subscribed' ||
                    session?.boundTreeStatus === 'limited'
                      ? '#ffd650'
                      : 'secondary.main',
                  cursor:
                    session?.boundTreeStatus === 'limited'
                      ? 'not-allowed !important'
                      : 'pointer',
                  display: {
                    xs: 'none',
                    md: 'block',
                  },
                }}
              >
                VIEW MORE <KeyboardArrowRightIcon />
              </Button>
            </div>
            <Swiper
              cssMode
              navigation
              mousewheel
              slidesPerView={largeScreen ? 5 : 3}
              spaceBetween={10}
              slidesPerGroup={2}
              modules={[Navigation, Pagination, Mousewheel, Keyboard]}
              className="netflix-slider"
              data-testid="courses-slider"
            >
              {mergeDeepWith(concat, [], courses)?.[section.id]?.map(
                (item: Course) => {
                  let isCompleted = completedCourses.some(
                    (cc) => cc === item.id,
                  )
                  let isAssigned = incompleteAssignmentCourses.includes(item.id)

                  return (
                    <SwiperSlide key={item.id} style={{ position: 'relative' }}>
                      {session?.boundTreeStatus === 'limited' && (
                        <LockIcon
                          sx={{
                            position: 'absolute',
                            zIndex: 1,
                            right: 5,
                            top: 5,
                            cursor: 'not-allowed',
                          }}
                        />
                      )}
                      {isCompleted && !isAssigned && completeMarker}
                      {isAssigned && assignedMarker}
                      <Box sx={{ bgcolor: 'background.default' }}>
                        <img
                          onClick={
                            session?.boundTreeStatus === 'limited'
                              ? noop
                              : () =>
                                  navigate(
                                    `/self-paced/course/${allCourses.find((x) => x.id === item.id)?.id}`,
                                  )
                          }
                          style={{
                            cursor:
                              session?.boundTreeStatus === 'limited'
                                ? 'not-allowed'
                                : 'pointer',
                            filter:
                              session?.boundTreeStatus === 'limited'
                                ? 'brightness(30%)'
                                : 'brightness(100%)',
                            opacity: isCompleted && !isAssigned ? 0.4 : 1,
                          }}
                          src={`${import.meta.env.VITE_CLOUDFRONT_URL}/public/${item.imageName}`}
                          alt={item.displayName}
                        />
                      </Box>
                    </SwiperSlide>
                  )
                },
              )}
            </Swiper>
          </div>
        ))}

      <ConfirmUpgrade />
      <Loading
        entities={[
          classesStatus,
          completedClassesStatus,
          completedCoursesStatus,
          coursesStatus,
          inProgressCoursesStatus,
          seriesStatus,
          incompleteAssignmentCoursesStatus,
        ]}
      />
    </>
  )
}
